import { Mutation } from "@apollo/client/react/components";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import moment from "moment";
import Link from "next/link";
import React, { createRef } from "react";
import { FormattedMessage } from "react-intl";
import useClipboard from "~/lib/hooks/useClipboard";
import CancelProductStackRequest from "~/lib/mutations/CancelProductStackRequest";
import {
  ICancelProductStackRequestMutation,
  ICancelProductStackRequestMutationVariables,
} from "~/lib/mutations/__generated__/CancelProductStackRequest.generated";
import VendorQuery from "~/lib/queries/VendorQuery";
import { IVendorQuery, IVendorQueryVariables } from "~/lib/queries/__generated__/VendorQuery.generated";
import getGovlaunchBaseUrl from "~/lib/utils/getGovlaunchBaseUrl";
import IntlRelativeTime from "~/src/shared/components/IntlRelativeTime";
import { IProduct } from "~/types/types";
import { useSelfie } from "~/components/auth/Auth";
import ClipboardIcon from "~/components/icons/ClipboardIcon";
import { Margin } from "~/components/spacings";
import Tooltip from "~/components/Tooltip";

interface IPendingInvitesProps {
  vendor: NonNullable<IVendorQuery["company"]>;
  productStackRequests: NonNullable<IVendorQuery["company"]>["pendingProductStackRequests"];
  product: Pick<IProduct, "_id" | "slug" | "name" | "logo">;
  onRequestClose?: (arg?: any) => void;
}

export default function PendingGovernmentInvites({
  vendor,
  productStackRequests,
  product,
  onRequestClose,
}: IPendingInvitesProps) {
  if (!productStackRequests) {
    return null;
  }

  const user = useSelfie();
  const inputRef = createRef<HTMLInputElement>();
  const [copied, setClipboard] = useClipboard(inputRef);

  if (!user) {
    return null;
  }

  return (
    <div>
      <h3
        css={{
          fontWeight: 600,
          fontSize: 18,
          color: palette.darkGray,
          margin: 0,
        }}
      >
        Pending Invites for {product.name} ({productStackRequests.length})
      </h3>

      <Mutation<ICancelProductStackRequestMutation, ICancelProductStackRequestMutationVariables>
        mutation={CancelProductStackRequest}
      >
        {(cancelProductStackRequest, { loading }) => (
          <Margin mt={24}>
            <div
              css={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, 70px)",
                gridGap: 32,
              }}
            >
              {productStackRequests.map((productStackRequest) => {
                const isSameHour = moment(productStackRequest.createdAt).isSame(moment().toDate(), "hour");

                return (
                  <Tooltip
                    key={productStackRequest._id}
                    backgroundColor={palette.innovatorBlue}
                    boxStyle={{
                      padding: "4px 8px",
                      border: "none",
                      borderRadius: 4,
                      boxShadow: "0 10px 40px 0 rgba(34, 35, 40, 0.2)",
                    }}
                    arrowSize={8}
                    render={() => (
                      <span
                        css={{
                          color: palette.white,
                          fontWeight: 500,
                          fontSize: 13,
                          lineHeight: 1.33,
                          letterSpacing: 1,
                        }}
                      >
                        {productStackRequest.government.name}
                      </span>
                    )}
                  >
                    {({ innerRef, bind }) => (
                      <Link
                        href="/governments/[governmentSlug]/[[...content]]"
                        as={`/governments/${productStackRequest.government.slug}`}
                        passHref={true}
                        legacyBehavior={true}
                      >
                        <a
                          {...bind}
                          ref={innerRef}
                          css={{
                            position: "relative",
                            display: "block",
                            color: "unset",
                            textDecoration: "none",
                          }}
                        >
                          <div
                            role="button"
                            css={{
                              cursor: "pointer",
                              position: "absolute",
                              top: -5,
                              zIndex: 10,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "100%",
                              right: -5,
                              width: 22,
                              height: 22,
                              background: palette.red,
                            }}
                            onClick={(event) => {
                              event.preventDefault();

                              if (!loading) {
                                cancelProductStackRequest({
                                  variables: {
                                    requestId: productStackRequest._id,
                                  },
                                  update: (proxy) => {
                                    proxy.writeQuery<IVendorQuery, IVendorQueryVariables>({
                                      query: VendorQuery,
                                      variables: {
                                        slug: vendor.slug,
                                        language: null,
                                      },
                                      data: {
                                        __typename: "Query",
                                        company: {
                                          ...vendor,
                                          pendingProductStackRequests: vendor.pendingProductStackRequests.filter(
                                            (request) => request._id !== productStackRequest._id,
                                          ),
                                        },
                                      },
                                    });
                                  },
                                }).then(() => {
                                  if (productStackRequests.length === 1 && onRequestClose) {
                                    onRequestClose();
                                  }
                                });
                              }
                            }}
                          >
                            <svg width={10} height={10}>
                              <path
                                fill="#FFF"
                                d="M6.188 5l3.566-3.565A.84.84 0 1 0 8.565.246L5 3.812 1.435.246A.84.84 0 1 0 .246 1.435L3.812 5 .246 8.565a.84.84 0 0 0 1.189 1.189L5 6.188l3.565 3.566a.84.84 0 1 0 1.189-1.189L6.188 5z"
                              />
                            </svg>
                          </div>

                          <ProfilePicture
                            size={70}
                            image={productStackRequest.government.logo}
                            name={productStackRequest.government.name}
                            css={{
                              display: "block",
                            }}
                          />

                          <span
                            css={{
                              display: "block",
                              marginTop: 2,
                              fontSize: 12,
                              color: palette.darkGray,
                              textAlign: "center",
                            }}
                          >
                            {isSameHour ? (
                              <FormattedMessage defaultMessage="just now" id="I90sbW" />
                            ) : (
                              <IntlRelativeTime value={productStackRequest.createdAt} />
                            )}
                          </span>
                        </a>
                      </Link>
                    )}
                  </Tooltip>
                );
              })}
            </div>
          </Margin>
        )}
      </Mutation>

      <Margin mt={24}>
        <div
          css={{
            fontWeight: 500,
            fontSize: 16,
            color: palette.darkGray,
          }}
        >
          <FormattedMessage
            defaultMessage="Send this invite link to your Government contacts to add {productName} to their stacks."
            id="9AeBqJ"
            values={{
              productName: product.name,
            }}
          />
        </div>
      </Margin>

      <Margin mt={24}>
        <input
          css={{
            boxSizing: "border-box",
            padding: 12,
            fontWeight: 500,
            fontSize: 14,
            color: palette.mediumGray,
            textAlign: "center",
            display: "block",
            fontFamily: "Courier",
            lineHeight: 1.44,
            backgroundColor: "transparent",
            border: `dashed 1px #48A0DA`,
            borderRadius: 4,
            outline: "none",
            WebkitAppearance: "none",
            width: "100%",
          }}
          value={`${getGovlaunchBaseUrl()}/signin?referral=${window.btoa(
            JSON.stringify({
              userId: user._id,
              productId: product._id,
              type: "product-stack-request",
            }),
          )}`}
          onChange={() => {}}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
          readOnly={true}
          ref={inputRef}
        />
      </Margin>

      <Margin
        mt={16}
        css={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <button
          css={{
            border: 0,
            outline: 0,
            background: "none",
            color: palette.primary,
            fontWeight: 500,
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            padding: 0,
            cursor: "pointer",
            margin: 0,
          }}
          type="button"
          onClick={() => {
            setClipboard();
          }}
        >
          <FormattedMessage defaultMessage="Copy invite link" id="rtACcE" />{" "}
          <span
            css={{
              marginLeft: 4,
            }}
          >
            <ClipboardIcon />
          </span>
        </button>

        {copied && (
          <span
            css={{
              marginLeft: 6,
              color: palette.greenSuccess,
            }}
          >
            <FormattedMessage defaultMessage="Copied" id="p556q3" />
          </span>
        )}
      </Margin>
    </div>
  );
}
